import { h, Component, createRef } from "preact";

import { Keyboard } from "../../foundation/enums";
import { shallowDiffer } from "../../foundation/utils/diff";
import { isCmdPressed, isShiftPressed } from "../../foundation/utils/keyboard";
import { keysOf } from "../../foundation/utils/object";
import { clearNativeSelection } from "../../foundation/utils/platform";

import MultiSelectGroupContext from "./MultiSelectGroupContext";
import { SelectboxRect, Selectbox } from "./Selectbox";
import { MouseOrigin, MultiSelectItem } from "./types";
import { boundsCollide, ComputedBounds, getBoundsForNode } from "./utils/bounds";

interface SelectionInfo<T> {
    firstSelectingItem: T | undefined;
    lastSelectingItem: T | undefined;
}

interface MultiSelectGroupProps<T extends string | number | symbol> {
    class?: string;
    selectedIds?: T[];
    enableDeselect?: boolean;
    resetOnStart?: boolean;
    deselectOnEsc?: boolean;
    allowCtrlClick?: boolean;
    allowShiftClick?: boolean;
    disabled?: boolean;
    getScrollContainer: () => HTMLElement | null;
    onSelectionDone?: (selectionIds: T[], selectionInfo: SelectionInfo<T>) => void;
    onClearSelection: () => void;
    onSelectionStart?: () => void;
    onSelectionEnd?: () => void;
}

type UnmountedItems<T extends string | number | symbol> = Record<T, ComputedBounds>;

interface State {
    containerScroll: { scrollTop: number; scrollLeft: number; };
    selectionStarted: boolean;
    selectionReady: boolean;
    ctrlPressed: boolean;
    shiftPressed: boolean;
    mouseDownOrigin: MouseOrigin | null;
}

class MultiSelectGroup<T extends string | number | symbol> extends Component<MultiSelectGroupProps<T>, State> {
    override state: State = {
        containerScroll: { scrollTop: 0, scrollLeft: 0 },
        selectionStarted: false,
        selectionReady: false,
        ctrlPressed: false,
        shiftPressed: false,
        mouseDownOrigin: null
    };

    multiSelectGroupRef = createRef<HTMLDivElement>();
    // Selectable items
    registry = new Set<MultiSelectItem<T>>();
    public registryMap = {} as Record<T, MultiSelectItem<T>>;

    // Selectbox & scroll
    scrollContainer: HTMLElement | null = null;
    selectBoxBounds: ComputedBounds | null = null;
    scrollBounds: DOMRect | null = null;
    maxScrollTop = 0;
    maxScrollLeft = 0;

    // Selection calculation & results
    selectingItems = new Set<MultiSelectItem<T>>();
    selectedItems = new Set<MultiSelectItem<T>>();
    unmountedItems: UnmountedItems<T> = {} as UnmountedItems<T>;
    firstSelectingItem: MultiSelectItem<T> | null = null;
    lastSelectingItem: MultiSelectItem<T> | null = null;

    itemActions = {
        deselecting: (item: MultiSelectItem<T>) => {
            item.updateSelectionState({ isSelecting: true });
            this.selectingItems.add(item);
            item.deselected = true;
        },
        selecting: (item: MultiSelectItem<T>) => {
            item.updateSelectionState({ isSelecting: true });
            this.selectingItems.add(item);

            this.firstSelectingItem ??= item;
            this.lastSelectingItem = item;
        },
        select: (item: MultiSelectItem<T>) => {
            item.updateSelectionState({ isSelected: true });
            this.selectedItems.add(item);
        },
        deselect: (item: MultiSelectItem<T>) => {
            item.updateSelectionState({ isSelected: false });
            this.selectedItems.delete(item);
        },
        toggleSelection: (item: MultiSelectItem<T>) => {
            if (item.selectionState.isSelected) {
                this.itemActions.deselect(item);
            } else {
                this.itemActions.select(item);
            }
        },
        removeSelecting: (item: MultiSelectItem<T>) => {
            item.updateSelectionState({ isSelecting: false });
            this.selectingItems.delete(item);
        },
        removeSelections: () => {
            for (const item of this.selectedItems.values()) {
                item.updateSelectionState({ isSelected: false });
                this.selectedItems.delete(item);
            }
        },
        createSelections: () => {
            for (const item of this.selectingItems.values()) {
                item.updateSelectionState({ isSelected: !item.selectionState.isSelected, isSelecting: false });
                if (item.selectionState.isSelected) {
                    this.selectedItems.delete(item);
                } else {
                    this.selectedItems.add(item);
                }
            }

            this.selectingItems.clear();
        }
    };

    get selectionInfo(): SelectionInfo<T> {
        return {
            firstSelectingItem: this.firstSelectingItem?.selectableId,
            lastSelectingItem: this.lastSelectingItem?.selectableId
        };
    }

    // #region MultiSelectGroupContext

    registerItem = (item: MultiSelectItem<T>) => {
        this.registry.add(item);
        this.registryMap[item.selectableId] = item;

        if (item.selectionState.isSelected) {
            this.selectedItems.add(item);
        }

        if (this.state.selectionStarted && this.unmountedItems[item.selectableId]) {
            delete this.unmountedItems[item.selectableId];
        }
    };

    unregisterItem = (item: MultiSelectItem<T>) => {
        this.registry.delete(item);
        delete this.registryMap[item.selectableId];
        this.selectingItems.delete(item);
        this.selectedItems.delete(item);

        if (this.state.selectionStarted && !this.unmountedItems[item.selectableId]) {
            if (!item.bounds) {
                this.updateItemBounds(item);
            }

            this.unmountedItems[item.selectableId] = item.bounds!;
        }
    };

    updateItemRegistry = (item: MultiSelectItem<T>) => {
        this.registryMap[item.selectableId] = item;
    };

    toggleSelection = (item: MultiSelectItem<T>) => {
        this.itemActions.toggleSelection(item);
        const items = this.orderSelectedItems();
        this.props.onSelectionDone?.(items, this.selectionInfo);
    };

    contextValue = {
        register: this.registerItem,
        unregister: this.unregisterItem,
        updateRegistry: this.updateItemRegistry,
        toggleSelection: this.toggleSelection
    };

    // #endregion

    saveContainerScroll = () => {
        const { scrollTop, scrollLeft } = this.scrollContainer!;
        this.setState({
            containerScroll: { scrollTop, scrollLeft }
        }, () => this.updateItemBoundsInRegistry());
    };

    clearSelection = () => {
        const { onClearSelection } = this.props;

        this.itemActions.removeSelections();
        this.selectedItems.clear();
        this.unmountedItems = {} as UnmountedItems<T>;
        this.firstSelectingItem = null;
        this.lastSelectingItem = null;

        onClearSelection();
    };

    updateRootBounds = () => {
        this.scrollBounds = this.scrollContainer!.getBoundingClientRect();
        this.maxScrollTop = this.scrollContainer!.scrollHeight - this.scrollContainer!.clientHeight;
        this.maxScrollLeft = this.scrollContainer!.scrollWidth - this.scrollContainer!.clientWidth;
    };

    updateItemBounds = (item: MultiSelectItem<T>) => {
        const { scrollTop, scrollLeft } = this.state.containerScroll;

        item.updateBounds({ scrollTop, scrollLeft });
    };

    updateItemBoundsInRegistry = () => {
        for (const item of this.registry.values()) {
            this.updateItemBounds(item);
        }
    };

    processAllCollisions = (selectBoxBounds: ComputedBounds) => {
        for (const item of this.registry.values()) {
            this.processCollision(item, selectBoxBounds);
        }
    };

    processCollision = (item: MultiSelectItem<T>, selectedBoxBounds: ComputedBounds) => {
        const bounds = item.bounds ?? getBoundsForNode(item.node!, this.scrollContainer!);
        const collides = boundsCollide(selectedBoxBounds, bounds, { withOffsetSize: true });

        const { enableDeselect } = this.props;
        const { isSelecting, isSelected } = item.selectionState;

        if (collides) {
            if (isSelected && enableDeselect) {
                this.itemActions.deselecting(item);
            } else if (!isSelecting && !isSelected && !item.deselected) {
                this.itemActions.selecting(item);
            }
        } else if (!collides && isSelecting) {
            if (this.selectingItems.has(item)) {
                this.itemActions.removeSelecting(item);
            }
        }
    };

    orderSelectedItems = () : T[] => {
        const unmountedSelectedItems: { selectableId: T; bounds: ComputedBounds; }[] = [];

        if (this.selectBoxBounds) {
            keysOf(this.unmountedItems).forEach(itemId => {
                const collides = boundsCollide(
                    this.selectBoxBounds!,
                    this.unmountedItems[itemId],
                    { withOffsetSize: true }
                );
                if (collides) {
                    unmountedSelectedItems.push({ selectableId: itemId, bounds: this.unmountedItems[itemId] });
                }
            });
        }

        const sortedSelectedItems = [...this.selectedItems, ...unmountedSelectedItems].sort((a, b) => {
            if (a.bounds && b.bounds) {
                if (a.bounds.top < b.bounds.top) {
                    return -1;
                }

                if (a.bounds.top === b.bounds.top) {
                    return a.bounds.left - b.bounds.left;
                }

                return 1;
            }

            return 0;
        });

        const mergedSelectedItemIds = new Set([
            ...sortedSelectedItems.map(item => item.selectableId)
        ]);

        return [...mergedSelectedItemIds];
    };

    updateSelectionStateOfItems = (selectedIds: T[]) => {
        const selectedIdsSet = new Set(selectedIds);

        for (const selectable of this.registry.values()) {
            const isCurrentlySelected = selectedIdsSet.has(selectable.selectableId);
            const wasSelected = this.selectedItems.has(selectable);

            if (isCurrentlySelected && !wasSelected) {
                this.selectedItems.add(selectable);
                selectable.updateSelectionState({ isSelected: true });
            } else if (!isCurrentlySelected && wasSelected) {
                this.selectedItems.delete(selectable);
                selectable.updateSelectionState({ isSelected: false });
            }
        }
    };

    handleSelectboxRectUpdate = (selectboxRect: SelectboxRect) => {
        const { x, y, width, height } = selectboxRect;

        this.selectBoxBounds = {
            top: y + this.scrollBounds!.top,
            left: x + this.scrollBounds!.left,
            width,
            height,
            offsetWidth: width || 1,
            offsetHeight: height || 1
        };

        this.processAllCollisions(this.selectBoxBounds);
    };

    handleMouseDown = (event: MouseEvent) => {
        const { disabled, resetOnStart, getScrollContainer } = this.props;
        const { selectionStarted, ctrlPressed, shiftPressed } = this.state;

        if (event.button !== 0 || selectionStarted || disabled) {
            return;
        }

        this.scrollContainer = getScrollContainer();
        if (!this.scrollContainer) {
            return;
        }

        if (resetOnStart && !ctrlPressed && !shiftPressed) {
            this.clearSelection();
        }

        this.updateRootBounds();

        const { scrollTop, scrollLeft } = this.scrollContainer!;

        this.setState({
            selectionStarted: true,
            selectionReady: false,
            containerScroll: { scrollTop, scrollLeft },
            mouseDownOrigin: {
                x: event.clientX - this.scrollBounds!.left + scrollLeft,
                y: event.clientY - this.scrollBounds!.top + scrollTop
            }
        }, () => {
            this.updateItemBoundsInRegistry();
            this.handleSelectionStart();
        });
    };

    handleMouseUp = () => {
        const { selectionReady, mouseDownOrigin } = this.state;
        if (selectionReady) {
            return;
        }

        // TODO-electron-tech-debt-yahya: both `selectionStarted` and `mouseDownOrigin` might not be needed. `mouseDownOrigin` should be enough
        this.setState({
            selectionStarted: false,
            selectionReady: true,
            mouseDownOrigin: null
        });

        this.handleSelectionEnd();

        // TODO-electron-tech-debt-yahya: Invesigate why this is needed
        if (!mouseDownOrigin) {
            return;
        }

        this.itemActions.createSelections();

        const selectedItemIds = this.orderSelectedItems();

        this.props.onSelectionDone?.(selectedItemIds, this.selectionInfo);

        this.firstSelectingItem = null;
        this.lastSelectingItem = null;

        for (const item of this.registry.values()) {
            item.deselected = false;
        }
    };

    handleBlur = () => {
        this.setState({ ctrlPressed: false, shiftPressed: false });
    };

    handleKeyDown = (event: KeyboardEvent) => {
        const { deselectOnEsc, allowCtrlClick, allowShiftClick } = this.props;

        if (deselectOnEsc && event.key === Keyboard.Key.Escape) {
            this.clearSelection();
        } else if (allowCtrlClick && isCmdPressed(event)) {
            this.setState({ ctrlPressed: true });
        } else if (allowShiftClick && isShiftPressed(event)) {
            this.setState({ shiftPressed: true });
        }
    };

    handleCancelSelection = (event: KeyboardEvent) => {
        const { deselectOnEsc } = this.props;
        if (deselectOnEsc && event.key === Keyboard.Key.Escape) {
            this.selectingItems.forEach(item => {
                this.itemActions.removeSelecting(item);
            });
            this.selectingItems.clear();

            this.setState({
                selectionStarted: false,
                selectionReady: true,
                mouseDownOrigin: null
            });
            this.handleSelectionEnd();
        }
    };

    handleKeyUp = (event: KeyboardEvent) => {
        const { allowCtrlClick, allowShiftClick } = this.props;

        if (allowCtrlClick && isCmdPressed(event)) {
            this.setState({ ctrlPressed: false });
        } else if (allowShiftClick && isShiftPressed(event)) {
            this.setState({ shiftPressed: false });
        }
    };

    handleSelectionStart = () => {
        const { onSelectionStart } = this.props;

        document.addEventListener("mouseup", this.handleMouseUp);
        document.addEventListener("keydown", this.handleCancelSelection);

        this.scrollContainer?.addEventListener("scroll", this.saveContainerScroll);

        clearNativeSelection();
        onSelectionStart?.();
    };

    handleSelectionEnd = () => {
        const { onSelectionEnd } = this.props;

        document.removeEventListener("mouseup", this.handleMouseUp);
        document.removeEventListener("keydown", this.handleCancelSelection);

        this.scrollContainer?.removeEventListener("scroll", this.saveContainerScroll);

        clearNativeSelection();
        onSelectionEnd?.();
    };

    override componentDidMount() {
        const {
            deselectOnEsc,
            allowCtrlClick,
            allowShiftClick,
            selectedIds
        } = this.props;

        this.multiSelectGroupRef.current!.addEventListener("mousedown", this.handleMouseDown);

        if (deselectOnEsc || allowCtrlClick || allowShiftClick) {
            window.addEventListener("blur", this.handleBlur);
            document.addEventListener("keydown", this.handleKeyDown);
        }

        if (allowCtrlClick || allowShiftClick) {
            document.addEventListener("keyup", this.handleKeyUp);
        }

        if (selectedIds && selectedIds.length > 0) {
            this.updateSelectionStateOfItems(selectedIds);
        }
    }

    override componentDidUpdate(prevProps: MultiSelectGroupProps<T>) {
        const { selectedIds: prevSelectedIds = [] } = prevProps;
        const { selectedIds = [] } = this.props;

        const selectedIdsChanged = shallowDiffer(selectedIds, prevSelectedIds);
        if (selectedIdsChanged) {
            this.updateSelectionStateOfItems(selectedIds);
        }
    }

    override componentWillUnmount() {
        const { deselectOnEsc, allowCtrlClick, allowShiftClick } = this.props;

        this.multiSelectGroupRef.current!.removeEventListener("mousedown", this.handleMouseDown);

        if (deselectOnEsc || allowCtrlClick || allowShiftClick) {
            window.removeEventListener("blur", this.handleBlur);
            document.removeEventListener("keydown", this.handleKeyDown);
        }

        if (allowCtrlClick || allowShiftClick) {
            document.removeEventListener("keyup", this.handleKeyUp);
        }

        this.handleSelectionEnd();

        this.selectedItems.clear();
        this.selectingItems.clear();
    }

    render() {
        const { class: cssClass, children } = this.props;
        const { mouseDownOrigin } = this.state;

        return (
            <MultiSelectGroupContext.Provider value={this.contextValue}>
                <div class={cssClass} ref={this.multiSelectGroupRef}>
                    {children}
                    {mouseDownOrigin && (
                        <Selectbox
                            scrollContainer={this.scrollContainer!}
                            maxScrollTop={this.maxScrollTop}
                            maxScrollLeft={this.maxScrollLeft}
                            mouseDownOrigin={mouseDownOrigin}
                            onRectUpdate={this.handleSelectboxRectUpdate}/>
                    )}
                </div>
            </MultiSelectGroupContext.Provider>
        );
    }
}

export default MultiSelectGroup;
export {
    SelectionInfo
};
